import * as models from './models'

export interface State {
  orgCharts: models.OrgChart.Lookup
}
export namespace State {
  export const INITIAL: State = {
    orgCharts: {
      current: {
        edges: [
          { childId: 'cto', parentId: 'ceo' },
          { childId: 'cfo', parentId: 'ceo' },
          { childId: 'dev1', parentId: 'cto' },
          { childId: 'dev2', parentId: 'cto' },
          { childId: 'acc1', parentId: 'cfo' },
        ],
        edits: {},
        history: [],
        nodes: [
          {
            id: 'ceo',
            name: 'CEO',
            person: 'John Smith',
            type: 'default',
          },
          {
            id: 'cto',
            name: 'CTO',
            person: 'Jane Doe',
            type: 'default',
          },
          {
            id: 'cfo',
            name: 'CFO',
            person: 'Bob Johnson',
            type: 'temporary',
          },
          {
            id: 'dev1',
            name: 'Senior Developer',
            person: 'Alice Brown',
            type: 'default',
          },
          {
            id: 'dev2',
            name: 'Junior Developer',
            person: 'Charlie Davis',
            type: 'future',
          },
          {
            id: 'acc1',
            name: 'Accountant',
            person: 'Diana Wilson',
            type: 'default',
          },
        ],
        undoCount: 0,
      },
    },
  }

  /**
   * Updates a specific {@link models.OrgChart} in {@link State}
   */
  export function patchOrgChart({
    orgChartId,
    state,
    apply,
  }: models.OrgChart.Identifier & {
    readonly state: State
    readonly apply:
      | models.OrgChart
      | ((orgChart: models.OrgChart | undefined) => models.OrgChart | undefined)
  }): State {
    const oldOrgChart = state.orgCharts[orgChartId]
    const newOrgChart = typeof apply === 'function' ? apply(oldOrgChart) : apply
    if (!newOrgChart) {
      return state
    }
    return {
      ...state,
      orgCharts: {
        ...state.orgCharts,
        [orgChartId]: newOrgChart,
      },
    }
  }

  /**
   * Updates a specific {@link models.OrgChart} in {@link State}
   */
  export function applyChangeToOrgChart({
    orgChartId,
    state,
    change: changeArg,
  }: models.OrgChart.Identifier & {
    readonly state: State
    readonly change:
      | models.Change
      | ((orgChart: models.OrgChart | undefined) => models.Change | undefined)
  }): State {
    return patchOrgChart({
      apply: oldOrgChart => {
        const change =
          typeof changeArg === 'function' ? changeArg(oldOrgChart) : changeArg
        if (!change) {
          return undefined
        }
        return {
          ...models.Change.appliedTo(
            // We used data from the existing org chart, if there is one.
            // Otherwise, we create empty data
            oldOrgChart ?? {
              edges: [],
              nodes: [],
            },
            change,
          ),
          currentlyEditingNodeId: oldOrgChart?.currentlyEditingNodeId,
          edits: oldOrgChart?.edits ?? {},
          history: [...(oldOrgChart?.history ?? []), change],
          // We always reset to 0 on any new change
          undoCount: 0,
        }
      },
      orgChartId,
      state,
    })
  }

  /**
   * Updates a specific {@link models.OrgChart} in {@link State}
   */
  export function moveThroughHistoryInOrgChart({
    orgChartId,
    state,
    steps,
  }: models.OrgChart.Identifier & {
    readonly state: State
    /**
     * 1 = undo
     *
     * -1 = redo
     */
    readonly steps: 1 | -1
  }): State {
    return State.patchOrgChart({
      apply: orgChart => {
        if (!orgChart) {
          return undefined
        }
        const oldUndoCount = orgChart.undoCount
        const newUndoCount = oldUndoCount + steps
        if (newUndoCount < 0 || newUndoCount > orgChart.history.length) {
          return undefined
        }
        // positive = undo, negative = redo
        const changeToApply =
          steps > 0
            ? models.Change.inverse(
                orgChart.history[orgChart.history.length - newUndoCount],
              )
            : orgChart.history[orgChart.history.length - oldUndoCount]

        return {
          ...orgChart,
          ...models.Change.appliedTo(orgChart, changeToApply),
          undoCount: newUndoCount,
        }
      },
      orgChartId,
      state,
    })
  }
}
